import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  Briefcase,
  Calculator,
  CheckCircle2,
  FileText,
  Handshake,
  Megaphone,
  Scale,
  ShieldCheck,
  Store,
  Tags,
} from "lucide-react";

export const Route = createFileRoute("/guia-del-traspaso")({
  head: () => ({
    meta: [
      { title: "Guía del traspaso — Bizaclia" },
      {
        name: "description",
        content:
          "Guía práctica para traspasar tu negocio o local comercial. Aprende a valorar, publicar y cerrar un traspaso de forma segura.",
      },
      { property: "og:title", content: "Guía del traspaso — Bizaclia" },
      {
        property: "og:description",
        content:
          "Todo lo que necesitas saber para traspasar tu local comercial con éxito.",
      },
      { property: "og:url", content: "/guia-del-traspaso" },
    ],
    links: [{ rel: "canonical", href: "/guia-del-traspaso" }],
  }),
  component: GuiaTraspasoPage,
});

const pasos = [
  {
    icon: Calculator,
    title: "1. Valora tu traspaso",
    text: "El precio de traspaso depende de la ubicación, el aforo, la facturación histórica, el mobiliario, la maquinaria y la antigüedad del negocio. Solicita una valoración profesional si tienes dudas.",
  },
  {
    icon: FileText,
    title: "2. Reúne la documentación",
    text: "Prepara la licencia de actividad, el contrato de alquiler vigente, las últimas facturas de suministros, el libro de inspección y un inventario detallado del mobiliario y equipamiento incluido.",
  },
  {
    icon: Megaphone,
    title: "3. Publica en Bizaclia",
    text: "Crea un anuncio completo: fotos del local, descripción del negocio, facturación aproximada, motivo del traspaso y lo que incluye (mobiliario, clientela, proveedores, know-how).",
  },
  {
    icon: Handshake,
    title: "4. Negocia con seriedad",
    text: "Responde rápido a las consultas, programa visitas presenciales y ten preparada toda la información que un comprador pueda solicitar. La transparencia genera confianza y acelera el cierre.",
  },
  {
    icon: Scale,
    title: "5. Firma el contrato de traspaso",
    text: "El contrato debe incluir precio, forma de pago, fecha de entrega, inventario anexo, cesión de licencias y autorización del propietario si el alquiler lo requiere. Recomendamos asesoría jurídica.",
  },
  {
    icon: Store,
    title: "6. Entrega y transición",
    text: "Formaliza el cambio de titularidad en suministros, comunica a proveedores y clientes la nueva gestión, y acompaña al nuevo titular durante los primeros días si se acordó un período de formación.",
  },
];

const consejos = [
  {
    icon: Tags,
    title: "Fija un precio realista",
    text: "Un traspaso sobrevalorado tarda meses en venderse y desgasta tu negocio. Compara con traspasos similares en tu zona y sector. Es mejor negociar desde un precio justo.",
  },
  {
    icon: ShieldCheck,
    title: "Verifica la solvencia del comprador",
    text: "Pide referencias bancarias o un aval. Asegúrate de que el comprador tiene capacidad para asumir la renta del local y el pago del traspaso acordado.",
  },
  {
    icon: Briefcase,
    title: "Protege la confidencialidad",
    text: "No reveles datos sensibles (facturación exacta, nombres de proveedores clave) hasta firmar un acuerdo de confidencialidad. Usa un NDA básico antes de compartir documentación sensible.",
  },
];

function GuiaTraspasoPage() {
  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Volver al inicio
      </Link>

      <header className="mb-10">
        <h1 className="text-4xl font-bold tracking-tight">Guía del traspaso</h1>
        <p className="mt-2 text-muted-foreground max-w-2xl">
          Todo lo que necesitas saber para traspasar tu local comercial o negocio de forma segura, rápida y al mejor precio.
        </p>
      </header>

      {/* Steps */}
      <section className="mb-14">
        <h2 className="text-2xl font-semibold mb-6">Pasos para traspasar tu negocio</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {pasos.map((p) => (
            <div
              key={p.title}
              className="rounded-xl border border-border bg-background p-5 transition-shadow hover:shadow-sm"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                  <p.icon className="h-4.5 w-4.5 text-primary" />
                </div>
                <h3 className="font-semibold">{p.title}</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tips */}
      <section className="mb-14">
        <h2 className="text-2xl font-semibold mb-6">Consejos clave</h2>
        <div className="space-y-4">
          {consejos.map((c) => (
            <div
              key={c.title}
              className="rounded-lg border border-border bg-background p-5"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 mt-0.5">
                  <c.icon className="h-4.5 w-4.5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">{c.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{c.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-14">
        <h2 className="text-2xl font-semibold mb-6">Preguntas frecuentes</h2>
        <div className="space-y-3">
          {[
            {
              q: "¿Qué incluye exactamente un traspaso?",
              a: "El traspaso es la transmisión de un negocio en funcionamiento. Suele incluir el derecho a explotar el local (si hay alquiler), mobiliario, maquinaria, know-how, cartera de clientes y, en ocasiones, proveedores y stock. Lo que se incluye debe quedar claro en el contrato.",
            },
            {
              q: "¿Necesito permiso del propietario para traspasar?",
              a: "Sí, en la mayoría de contratos de alquiler comercial se requiere la autorización escrita del propietario para ceder la titularidad del contrato al nuevo inquilino. Revisa tu contrato y negocia esta autorización con antelación.",
            },
            {
              q: "¿Cómo se fija el precio de un traspaso?",
              a: "Se valora el potencial del negocio: facturación, beneficios, ubicación, afluencia, antigüedad, mobiliario incluido y condiciones del alquiler. Un local con alquiler por debajo de mercado tiene más valor de traspaso.",
            },
            {
              q: "¿Qué impuestos debo pagar al traspasar?",
              a: "El traspaso de un negocio tiene implicaciones fiscales (IVA, IRPF, transmisiones patrimoniales dependiendo de la forma jurídica). Te recomendamos consultar con un asesor fiscal para optimizar la operación.",
            },
          ].map((faq) => (
            <details
              key={faq.q}
              className="rounded-lg border border-border bg-background group"
            >
              <summary className="flex cursor-pointer items-center justify-between p-5 text-sm font-semibold select-none">
                {faq.q}
                <span className="ml-4 text-muted-foreground group-open:rotate-180 transition-transform">
                  ▼
                </span>
              </summary>
              <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">
                {faq.a}
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="rounded-xl border border-border bg-background p-6 md:p-8 text-center">
        <h2 className="text-xl font-semibold">¿Quieres traspasar tu negocio?</h2>
        <p className="mt-2 text-muted-foreground text-sm max-w-lg mx-auto">
          Publica tu anuncio gratis en Bizaclia y llega a miles de emprendedores buscando negocios en marcha.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/mybizaclia"
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Publicar traspaso
          </Link>
          <Link
            to="/contacto"
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Hablar con un asesor
          </Link>
        </div>
      </section>
    </main>
  );
}
